import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Locale, decimalMaskNumberConfigOverride } from '@wdx/shared/utils';
import { NgxCurrencyConfig } from 'ngx-currency';
import { startWith, take, takeUntil } from 'rxjs/operators';
import { BaseWdxFormControlClass } from '../../../abstract-classes/base-form-control.class';
import { BespokeValidators } from '../../../validators';
import {
    convertDurationToUi,
    durationApiToUiHours,
} from './duration-conversion.util';
import { AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
    selector: 'wdx-ff-duration-control',
    templateUrl: './form-duration-control.component.html',
    standalone: false,
})
export class FormDurationControlComponent
    extends BaseWdxFormControlClass
    implements OnInit, OnChanges
{
    private businessDayLengthHours =
        this.dynamicDataService.getBusinessDayLengthHours();
    public decimalMaskNumberConfigOverride = decimalMaskNumberConfigOverride;
    public timeUnits: { value: string; label: string }[] =
        this.businessDayLengthHours && this.businessDayLengthHours > 0
            ? [
                  {
                      value: 'hours',
                      label: this.translationsService.translateTokenisedString(
                          'ENUM_DURATIONTYPE_HOURS',
                      ),
                  },
                  {
                      value: 'days',
                      label: this.translationsService.translateTokenisedString(
                          'ENUM_DURATIONTYPE_DAYS',
                      ),
                  },
              ]
            : [
                  {
                      value: 'hours',
                      label: this.translationsService.translateTokenisedString(
                          'ENUM_DURATIONTYPE_HOURS',
                      ),
                  },
              ];
    public timeUnit =
        this.businessDayLengthHours && this.businessDayLengthHours > 0
            ? this.timeUnits[1]
            : this.timeUnits[0];
    public timeValue: number | null = null;
    private smallestUnitSeconds = 900;
    // we need to keep track of the formControl we are subscribing to, as it can change when
    // this control is used in a subform, in that case we need to rescribe to the new formControl
    private formControlSubscribedTo: AbstractControl | undefined;
    private formControlValueChangesSub: Subscription | undefined;

    ngOnInit(): void {
        this.dynamicDataService
            .getMeLocale()
            .pipe(take(1))
            .subscribe((locale: Locale) => {
                this.decimalMaskNumberConfigOverride = {
                    ...decimalMaskNumberConfigOverride,
                    decimal: locale?.numberFormat
                        ?.numberDecimalSeparator as string,
                    thousands: locale?.numberFormat
                        ?.numberGroupSeparator as string,
                };
            });
        this.timeUnit =
            this.businessDayLengthHours && this.businessDayLengthHours > 0
                ? this.timeUnits[1]
                : this.timeUnits[0];
        this.timeValue = this.convertToUi(this.formControl?.value);
        this.setupFromControlValueListener();

        this.formControl?.addValidators([
            BespokeValidators.mustBeMultipleOf(this.smallestUnitSeconds),
        ]);
        this.formControl?.updateValueAndValidity();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['formElement'].currentValue) {
            this.setupFromControlValueListener();
        }
    }

    timeValueChange(value: number) {
        this.formControl?.markAsTouched();
        this.formControl?.markAsDirty();
        this.formControl?.setValue(this.convertToApi(value));
    }

    timeUnitChange(): void {
        this.timeValue = this.convertToUi(this.formControl?.value);
    }

    convertToUi(value: number): number | null {
        return convertDurationToUi(
            value,
            this.timeUnit?.value === 'days'
                ? this.businessDayLengthHours
                : undefined,
        );
    }

    private convertToApi(value: number): number | null {
        if (typeof value !== 'number') {
            return null;
        }
        if (this.timeUnit?.value === 'days' && this.businessDayLengthHours) {
            return (
                (value || 0) *
                durationApiToUiHours *
                this.businessDayLengthHours
            );
        }

        return (value || 0) * durationApiToUiHours;
    }

    private setupFromControlValueListener() {
        if (this.formControlSubscribedTo === this.formControl) {
            return;
        }
        this.formControlValueChangesSub?.unsubscribe();
        this.formControlSubscribedTo = this.formControl;
        this.formControlValueChangesSub = this.formControl?.valueChanges
            ?.pipe(
                takeUntil(this.destroyed$),
                startWith(this.formControl?.value),
            )
            ?.subscribe((value) => {
                this.timeValue = this.convertToUi(value);
            });
    }
}
